import { ImageViewerImage } from "@omni/types/api/product/bff/shared/imageViewerImage.model";
import { ImageViewerVideo } from "@omni/types/api/product/bff/shared/imageViewerVideo.model";
import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import { Button } from "@omni/ui/components/Button/button.component";
import { cn } from "@omni/utils/styles";
import { useEffect, useMemo, useState } from "react";
import { Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperCore } from "swiper/types";

import { Modal } from "@/components/General/Modal/modal";
import { SlideThumbnail } from "@/components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@/components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";
import { ImageViewerFullWidth } from "@/components/Product/ImageViewerFullWidth/imageViewerFullWidth.container";
import { Image } from "@/components/Product/ProductImage/imageWrapper/image/image.component";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import { useGranite } from "@/redux/modules/value/translations/translations.provider";
import { DamImage } from "@/utils/dam.utils";
import { EmptyImages } from "@/utils/enums";
import { stopVideo } from "@/utils/imageViewer";

type Props = {
    title?: string;
    images: Array<ImageViewerImage>;
    videos: Array<ImageViewerVideo>;
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    usesDefaultImage: boolean;
    defaultVideoThumbnailPath?: string;
    ecoChequesAccepted: boolean;
};

const ImageViewer = ({
    title,
    images,
    videos,
    usesDefaultImage,
    setActiveIndex,
    activeIndex,
    defaultVideoThumbnailPath,
    ecoChequesAccepted
}: Props) => {
    const { t } = useGranite();

    const [modalOpen, setModalOpen] = useState(false);
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const mediaList = useMemo(() => [...images, ...videos], [images, videos]);

    const amount = mediaList.length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo();
        };

        window.addEventListener("hashchange", hashChangeHandler);
        return () =>
            window.removeEventListener("hashchange", hashChangeHandler);
    }, []);

    useEffect(() => {
        if (mainSwiper && mainSwiper.activeIndex !== activeIndex) {
            mainSwiper.slideTo(activeIndex);
        }
    }, [activeIndex, mainSwiper]);

    const showSlider =
        images.length > 1 || (images.length >= 1 && videos.length > 0);

    const handleOnZoom = () => {
        setModalOpen(true);
        mainSwiper && setActiveIndex(mainSwiper.activeIndex);
    };

    return (
        <div className={cn("tw:relative tw:pr-4 tw:mb-5")}>
            <div className={cn("tw:pb-2")}>
                <Swiper
                    modules={[Thumbs]}
                    thumbs={{ swiper: thumbSwiper }}
                    spaceBetween={10}
                    slidesPerView={1}
                    onActiveIndexChange={(swiper) => {
                        setActiveIndex(swiper.activeIndex);
                        stopVideo();
                    }}
                    onSwiper={setMainSwiper}
                >
                    {images
                        ?.filter((img) => !!img.main)
                        .map((image, index) => (
                            <SwiperSlide
                                key={`main${index}`}
                                tag={"button"}
                                onClick={handleOnZoom}
                            >
                                {ecoChequesAccepted && (
                                    <img
                                        className={cn(
                                            "tw:block tw:h-6 tw:absolute tw:top-0 tw:right-0"
                                        )}
                                        src={DamImage.icon.label.eco()}
                                        alt={"ecocheques"}
                                    />
                                )}
                                <Image
                                    className={"tw:w-full"}
                                    lazyLoad={index > 0}
                                    altText={image.altText}
                                    defaultImageUrl={EmptyImages.Size_120_90}
                                    medias={[
                                        {
                                            altText: image.altText,
                                            url: image.main!,
                                            format: ProductImageRendition.XS
                                        }
                                    ]}
                                    rendition={ProductImageRendition.XS}
                                />
                            </SwiperSlide>
                        ))}
                    {videos?.map((video, index) => (
                        <SwiperSlide
                            key={`main-video-${index}`}
                            className={cn(
                                "tw:flex tw:justify-center tw:items-center tw:w-full tw:p-0 tw:overflow-hidden",
                                "tw:before:pt-[56.25%] tw:before:block tw:before:content-['']"
                            )}
                        >
                            <iframe
                                className={cn(
                                    "product-video",
                                    "tw:block tw:border-none tw:w-full tw:h-full tw:absolute tw:top-0 tw:left-0"
                                )}
                                id={`imageViewerIframe-${index}`}
                                src={video.main}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            {!usesDefaultImage && (
                <div
                    className={cn(
                        "tw:text-center tw:pb-2",
                        "tw:max-md:absolute tw:max-md:top-0 tw:max-md:right-4 tw:max-md:z-1",
                        {
                            "tw:pt-12 tw:md:pt-0": ecoChequesAccepted
                        }
                    )}
                    onClick={handleOnZoom}
                >
                    <Button
                        color={"link"}
                        className={cn("tw:p-0")}
                        onClick={handleOnZoom}
                    >
                        <i
                            className={cn(
                                "las la-search-plus la-lg",
                                "tw:max-md:text-3xl"
                            )}
                        />
                        <span className={cn("tw:ml-1", "tw:max-md:hidden")}>
                            {t("productDetail.imageViewer.link.zoom")}
                        </span>
                    </Button>
                </div>
            )}
            {showSlider && (
                <div>
                    <div
                        className={cn(
                            "tw:hidden tw:relative tw:items-center",
                            "tw:md:flex"
                        )}
                    >
                        <SwiperNavButton
                            disabled={activeIndex === 0}
                            icon="left"
                            onClick={() => setActiveIndex(activeIndex - 1)}
                        />
                        <Swiper
                            onSwiper={setThumbSwiper}
                            spaceBetween={10}
                            slidesPerView={"auto"}
                            threshold={2}
                            watchSlidesProgress={true}
                            className={cn("tw:w-full tw:grow")}
                        >
                            {mediaList.map((media, index) => (
                                <SwiperSlide
                                    tag="button"
                                    key={`thumb${index}`}
                                    className={cn(
                                        "tw:my-1 tw:w-fit tw:h-12.5 tw:bg-transparent",
                                        {
                                            "tw:border tw:border-grey-light":
                                                index === activeIndex,
                                            "tw:border-none":
                                                index !== activeIndex,
                                            "tw:pr-2.5": !mainSwiper
                                        }
                                    )}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={index > 0}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                        </Swiper>
                        <SwiperNavButton
                            disabled={activeIndex + 1 === amount}
                            icon="right"
                            onClick={() => setActiveIndex(activeIndex + 1)}
                        />
                    </div>
                    <div
                        className={cn(
                            "tw:flex tw:md:hidden tw:items-center tw:justify-center"
                        )}
                    >
                        <SwiperNavButton
                            className={cn("tw:mr-4")}
                            disabled={activeIndex === 0}
                            icon={"left"}
                            onClick={() => setActiveIndex(activeIndex - 1)}
                        />
                        <p className={cn("tw:m-0")}>
                            {`${activeIndex + 1} van ${amount}`}
                        </p>
                        <SwiperNavButton
                            className={cn("tw:ml-4")}
                            disabled={activeIndex + 1 === amount}
                            icon="right"
                            onClick={() => setActiveIndex(activeIndex + 1)}
                        />
                    </div>
                </div>
            )}
            <Modal
                modalType={ModalType.ImageViewer}
                withFooter={false}
                size={"xl"}
                sheetDetent={"default"}
                title={title || t("modal.image-viewer.title")}
                open={modalOpen}
                setOpen={setModalOpen}
            >
                <ImageViewerFullWidth
                    className={cn("tw:overflow-hidden tw:w-full")}
                    images={images}
                    videos={videos}
                    defaultVideoThumbnailPath={defaultVideoThumbnailPath}
                />
            </Modal>
        </div>
    );
};

export { ImageViewer };
